import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Form, Input, Button, message, Card, Result, Spin, Progress, Alert } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import axios from "axios";

const ResetPasswordScreen = () => {
    const { token } = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [isValid, setIsValid] = useState(null);
    const [formError, setFormError] = useState(null); // error at bottom of card
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        checks: {
            minLength: false,
            hasUpper: false,
            hasLower: false,
            hasNumber: false,
            hasSpecial: false
        }
    });

    const [form] = Form.useForm();

    useEffect(() => {
        const verifyToken = async () => {
            try {
                await axios.get(`/auth/verify-reset-token/${token}`);
                setIsValid(true);
            } catch (err) {
                setIsValid(false);
                setTimeout(() => history.push("/invalid-token"), 2500);
            }
        };
        verifyToken();
    }, [token, history]);
     
    // Password strength analyzer
    const analyzePasswordStrength = (password) => {
        if (!password) {
            setPasswordStrength({
                score: 0,
                checks: {
                    minLength: false,
                    hasUpper: false,
                    hasLower: false,
                    hasNumber: false,
                    hasSpecial: false
                }
            });
            return;
        }

        const checks = {
            minLength: password.length >= 8,
            hasUpper: /[A-Z]/.test(password),
            hasLower: /[a-z]/.test(password),
            hasNumber: /\d/.test(password),
            hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
        };

        const score = Object.values(checks).filter(Boolean).length;
        setPasswordStrength({ score, checks });
    };

    const getStrengthColor = () => {
        if (passwordStrength.score <= 2) return "#ff4d4f";
        if (passwordStrength.score <= 3) return "#faad14";
        if (passwordStrength.score <= 4) return "#1890ff";
        return "#52c41a";
    };

    const getStrengthText = () => {
        if (passwordStrength.score <= 2) return "Weak";
        if (passwordStrength.score <= 3) return "Fair";
        if (passwordStrength.score <= 4) return "Good";
        return "Strong";
    };

    const onFinish = async (values) => {
        setFormError(null); // reset error

        if (values.newPassword !== values.confirmPassword) {
            return setFormError("Passwords do not match!");
        }

        if (passwordStrength.score < 5) {
            return setFormError("Please meet all password requirements before proceeding.");
        }

        if (values.currentPassword && values.currentPassword === values.newPassword) {
            return setFormError("New password cannot be the same as current password.");
        }

        try {
            setLoading(true);
            await axios.post(`/auth/reset/${token}`, {
                newPassword: values.newPassword,
                confirmPassword: values.confirmPassword,
            });
            message.success("Password reset successful!");
            if (localStorage.getItem("auth")) {
                history.push("/employees");
            } else {
                history.push("/");
            }
        } catch (err) {
            console.error(err);
            const errorMessage = err.response?.data?.message || "Reset failed. Please try again.";
            setFormError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = (e) => {
        analyzePasswordStrength(e.target.value);
    };

    if (isValid === null) {
        return (
            <div style={{ display: "flex", justifyContent: "center", marginTop: "20%" }}>
                <Spin tip="Checking link..." size="large" />
            </div>
        );
    }

    if (!isValid) {
        return (
            <Result
                status="error"
                title="Invalid or Expired Link"
                subTitle="This reset password link is no longer valid. Redirecting..."
                style={{ marginTop: "10%" }}
            />
        );
    }

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "5%",
                padding: "0 16px",
            }}
        >
            <Card style={{ maxWidth: 480, width: "100%" }}>
                <h2 style={{ textAlign: "center", marginBottom: 24 }}>
                    Reset Your Password
                </h2>
                <Alert
                    message="Password Requirements"
                    description="Your new password must meet all security requirements listed below."
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.Item
                        label="New Password"
                        name="newPassword"
                        rules={[{ required: true, message: "Please enter new password" }]}
                        hasFeedback
                    >
                        <Input.Password
                            onChange={handlePasswordChange}
                            placeholder="Enter your new password"
                        />
                    </Form.Item>

                    {/* Password Strength Indicator - always visible */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ fontSize: "12px", fontWeight: 500 }}>Password Strength:</span>
                            <span
                                style={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: getStrengthColor(),
                                }}
                            >
                                {getStrengthText()}
                            </span>
                        </div>
                        <Progress
                            percent={(passwordStrength.score / 5) * 100}
                            strokeColor={getStrengthColor()}
                            showInfo={false}
                            size="small"
                        />
                    </div>

                    {/* Password Requirements - always visible */}
                    <div
                        style={{
                            backgroundColor: "#f9f9f9",
                            padding: 12,
                            borderRadius: 6,
                            marginBottom: 16,
                            border: "1px solid #d9d9d9",
                        }}
                    >
                        <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: 8, color: "#666" }}>
                            Password Requirements:
                        </div>
                        {[
                            { key: "minLength", text: "At least 8 characters" },
                            { key: "hasUpper", text: "One uppercase letter (A-Z)" },
                            { key: "hasLower", text: "One lowercase letter (a-z)" },
                            { key: "hasNumber", text: "One number (0-9)" },
                            { key: "hasSpecial", text: "One special character (!@#$%^&*)" },
                        ].map((req) => (
                            <div
                                key={req.key}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "11px",
                                    color: passwordStrength.checks[req.key] ? "#52c41a" : "#ff4d4f",
                                    marginBottom: 4,
                                }}
                            >
                                {passwordStrength.checks[req.key] ? (
                                    <CheckOutlined style={{ marginRight: 6 }} />
                                ) : (
                                    <CloseOutlined style={{ marginRight: 6 }} />
                                )}
                                {req.text}
                            </div>
                        ))}
                    </div>

                    <Form.Item
                        label="Confirm Password"
                        name="confirmPassword"
                        dependencies={["newPassword"]}
                        hasFeedback
                        rules={[
                            { required: true, message: "Please confirm your password" },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue("newPassword") === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error("Passwords do not match!"));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="Confirm your new password" />
                    </Form.Item>

                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                        disabled={passwordStrength.score < 5}
                        style={{
                            backgroundColor: passwordStrength.score === 5 ? "#1890ff" : "#d9d9d9",
                            borderColor: passwordStrength.score === 5 ? "#1890ff" : "#d9d9d9",
                        }}
                    >
                        Reset Password
                    </Button>

                    {/* Error shown at bottom of card */}
                    {formError && (
                        <Alert
                            message={formError}
                            type="error"
                            showIcon
                            style={{ marginTop: 16 }}
                        />
                    )}
                </Form>
            </Card>
        </div>
    );
};

export default ResetPasswordScreen;